// File: src/components/pages/payments/PaymentHistory.jsx
// Full payment history for a selected student — all statuses

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  User, Calendar, CreditCard, Package, Hash,
  AlertCircle, RefreshCw, ChevronDown, ChevronUp
} from 'lucide-react';
import { paymentAPI, studentAPI } from '../../../services/api';
import '../../../styles/unified.css';

const STATUS_META = {
  Claimed:   { label: 'Claimed',   bg: '#fff3cd', color: '#856404', border: '#ffc107' },
  Confirmed: { label: 'Confirmed', bg: '#cff4fc', color: '#055160', border: '#0dcaf0' },
  Adjusted:  { label: 'Adjusted',  bg: '#d1e7dd', color: '#0a3622', border: '#198754' },
  Rejected:  { label: 'Rejected',  bg: '#f8d7da', color: '#842029', border: '#dc3545' },
};

const fmt       = (d) => !d ? '—' : new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
const fmtAmount = (n) => typeof n === 'number' ? `£${n.toFixed(2)}` : '—';

const PaymentHistory = () => {
  const { getAccessTokenSilently } = useAuth0();

  const [students,          setStudents]          = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [payments,          setPayments]          = useState([]);
  const [loading,           setLoading]           = useState(false);
  const [error,             setError]             = useState(null);
  const [expandedId,        setExpandedId]        = useState(null);
  const [sortField,         setSortField]         = useState('paymentDate');
  const [sortDir,           setSortDir]           = useState('desc');

  // Load students for dropdown
  useEffect(() => {
    studentAPI.getStudentsByStatus('active', getAccessTokenSilently)
      .then(r => setStudents(r.data || []))
      .catch(console.error);
  }, [getAccessTokenSilently]);

  // Load payment history when student changes
  const loadHistory = useCallback(async () => {
    if (!selectedStudentId) { setPayments([]); return; }
    setLoading(true); setError(null);
    try {
      const res = await paymentAPI.getPaymentsByStudent(selectedStudentId, getAccessTokenSilently);
      setPayments(res.data || res || []);
    } catch (e) {
      setError(e.message || 'Failed to load payment history');
    } finally {
      setLoading(false);
    }
  }, [selectedStudentId, getAccessTokenSilently]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  // ── Sorting ───────────────────────────────────────────────────────────────
  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const sorted = [...payments].sort((a, b) => {
    let av = a[sortField], bv = b[sortField];
    if (sortField === 'paymentDate') { av = new Date(av); bv = new Date(bv); }
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  // ── Status summary counts ─────────────────────────────────────────────────
  const statusCounts = payments.reduce((acc, p) => {
    acc[p.status] = (acc[p.status] || 0) + 1;
    return acc;
  }, {});

  const totalPaid = payments
    .filter(p => ['Confirmed', 'Adjusted'].includes(p.status))
    .reduce((sum, p) => sum + (p.paymentAmount || 0), 0);

  // ── Sort icon ─────────────────────────────────────────────────────────────
  const SortIcon = ({ field }) =>
    sortField !== field ? null :
    sortDir === 'asc'
      ? <ChevronUp size={13} style={{ verticalAlign: '-2px' }} />
      : <ChevronDown size={13} style={{ verticalAlign: '-2px' }} />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

      {/* Student selector */}
      <div className="card">
        <div className="card-body" style={{ padding: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <User size={18} color="#667eea" />
            <label style={{ fontWeight: 600, fontSize: '0.95rem' }}>Select Student</label>
            <select
              value={selectedStudentId}
              onChange={e => setSelectedStudentId(e.target.value)}
              className="form-select"
              style={{ minWidth: '220px' }}
            >
              <option value="">— Choose a student —</option>
              {students.map(s => (
                <option key={s.studentId} value={s.studentId}>
                  {s.firstName} {s.lastName}
                </option>
              ))}
            </select>
            <button
              onClick={loadHistory}
              disabled={loading || !selectedStudentId}
              className="btn-secondary"
              title="Refresh"
            >
              <RefreshCw size={14} />
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="booking-error-banner">
          <AlertCircle size={18} /><span>{error}</span>
        </div>
      )}

      {/* Summary bar */}
      {selectedStudentId && !loading && payments.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: '0.75rem'
        }}>
          {Object.entries(STATUS_META).map(([status, meta]) =>
            statusCounts[status] ? (
              <div key={status} style={{
                padding: '0.75rem 1rem',
                background: meta.bg,
                border: `1px solid ${meta.border}`,
                borderRadius: '0.5rem',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '1.4rem', fontWeight: 700, color: meta.color }}>
                  {statusCounts[status]}
                </div>
                <div style={{ fontSize: '0.78rem', color: meta.color, fontWeight: 500 }}>
                  {meta.label}
                </div>
              </div>
            ) : null
          )}
          <div style={{
            padding: '0.75rem 1rem',
            background: '#f0fdf4',
            border: '1px solid #86efac',
            borderRadius: '0.5rem',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#15803d' }}>
              {fmtAmount(totalPaid)}
            </div>
            <div style={{ fontSize: '0.78rem', color: '#15803d', fontWeight: 500 }}>Total Paid</div>
          </div>
        </div>
      )}

      {/* Payment list */}
      {loading ? (
        <div className="loading-state"><div className="spinner" /></div>
      ) : !selectedStudentId ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#9ca3af', fontSize: '0.9rem' }}>
          Select a student to view their payment history
        </div>
      ) : payments.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#9ca3af', fontSize: '0.9rem' }}>
          No payment records found for this student
        </div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          {/* Table header */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '2rem 1.5fr 1fr 1fr 1fr 0.8fr 0.8fr',
            gap: '0.5rem',
            padding: '0.75rem 1rem',
            background: '#f9fafb',
            borderBottom: '1px solid #e5e7eb',
            fontSize: '0.78rem',
            fontWeight: 600,
            color: '#6b7280',
            textTransform: 'uppercase',
            letterSpacing: '0.04em'
          }}>
            <span />
            <span style={{ cursor: 'pointer' }} onClick={() => toggleSort('paymentDate')}>
              Date <SortIcon field="paymentDate" />
            </span>
            <span style={{ cursor: 'pointer' }} onClick={() => toggleSort('paymentOption')}>
              Option <SortIcon field="paymentOption" />
            </span>
            <span style={{ cursor: 'pointer' }} onClick={() => toggleSort('paymentAmount')}>
              Amount <SortIcon field="paymentAmount" />
            </span>
            <span>Classes / Duration</span>
            <span>Method</span>
            <span style={{ cursor: 'pointer' }} onClick={() => toggleSort('status')}>
              Status <SortIcon field="status" />
            </span>
          </div>

          {/* Rows */}
          {sorted.map(p => {
            const meta     = STATUS_META[p.status] || { label: p.status, bg: '#f3f4f6', color: '#374151', border: '#d1d5db' };
            const expanded = expandedId === p.paymentId;
            return (
              <div key={p.paymentId} style={{ borderBottom: '1px solid #f3f4f6' }}>
                {/* Main row */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '2rem 1.5fr 1fr 1fr 1fr 0.8fr 0.8fr',
                    gap: '0.5rem',
                    padding: '0.875rem 1rem',
                    alignItems: 'center',
                    cursor: 'pointer',
                    background: expanded ? '#fafafa' : 'white',
                    transition: 'background 0.1s'
                  }}
                  onClick={() => setExpandedId(expanded ? null : p.paymentId)}
                >
                  <span style={{ color: '#9ca3af' }}>
                    {expanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                  </span>
                  <span style={{ fontSize: '0.88rem', fontWeight: 500 }}>{fmt(p.paymentDate)}</span>
                  <span style={{ fontSize: '0.88rem' }}>{p.paymentOption}</span>
                  <span style={{ fontSize: '0.88rem', fontWeight: 600 }}>{fmtAmount(p.paymentAmount)}</span>
                  <span style={{ fontSize: '0.82rem', color: '#6b7280' }}>
                    {p.numberOfClasses ? `${p.numberOfClasses} class${p.numberOfClasses !== 1 ? 'es' : ''}` : '—'}
                    {p.lessonDuration ? ` · ${p.lessonDuration}h` : ''}
                  </span>
                  <span style={{ fontSize: '0.82rem', color: '#6b7280' }}>{p.paymentMethod || '—'}</span>
                  <span>
                    <span style={{
                      fontSize: '0.75rem', fontWeight: 700, padding: '2px 9px',
                      borderRadius: '99px', background: meta.bg,
                      color: meta.color, border: `1px solid ${meta.border}`,
                      whiteSpace: 'nowrap'
                    }}>
                      {meta.label}
                    </span>
                  </span>
                </div>

                {/* Expanded detail */}
                {expanded && (
                  <div style={{
                    padding: '0.75rem 1rem 1rem 3rem',
                    background: '#fafafa',
                    borderTop: '1px solid #f3f4f6',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                    gap: '0.75rem',
                    fontSize: '0.83rem'
                  }}>
                    <div>
                      <div style={{ color: '#9ca3af', marginBottom: '2px' }}>Serial #</div>
                      <div style={{ fontWeight: 500 }}>{p.serialNumber || '—'}</div>
                    </div>
                    <div>
                      <div style={{ color: '#9ca3af', marginBottom: '2px' }}>Original Amount</div>
                      <div style={{ fontWeight: 500 }}>{fmtAmount(p.originalAmount)}</div>
                    </div>
                    {p.discountAmount > 0 && (
                      <div>
                        <div style={{ color: '#9ca3af', marginBottom: '2px' }}>Discount ({p.discountType})</div>
                        <div style={{ fontWeight: 500, color: '#10b981' }}>−{fmtAmount(p.discountAmount)}</div>
                      </div>
                    )}
                    <div>
                      <div style={{ color: '#9ca3af', marginBottom: '2px' }}>Hourly Rate</div>
                      <div style={{ fontWeight: 500 }}>{p.hourlyRate ? `£${p.hourlyRate}/hr` : '—'}</div>
                    </div>
                    {p.notes && (
                      <div style={{ gridColumn: '1 / -1' }}>
                        <div style={{ color: '#9ca3af', marginBottom: '2px' }}>Notes</div>
                        <div style={{ fontStyle: 'italic' }}>{p.notes}</div>
                      </div>
                    )}
                    {/*}
                    {p.approvedBy && (
                      <div>
                        <div style={{ color: '#9ca3af', marginBottom: '2px' }}>Approved by</div>
                        <div style={{ fontWeight: 500 }}>{p.approvedBy}</div>
                      </div>
                    )}*/}
                    {p.processedAt && (
                      <div>
                        <div style={{ color: '#9ca3af', marginBottom: '2px' }}>Processed at</div>
                        <div style={{ fontWeight: 500 }}>{fmt(p.processedAt)}</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PaymentHistory;